/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../auth/AuthenticationContext';
import createScenario from '../../../../domain/use_cases/scenarios/createScenario';
import ScenarioBusinessPriority from '../../../../domain/enums/scenario/ScenarioBusinessPriority';
import ContactQualificationAiQualification from '../../../../domain/enums/contact/contact_qualification_ai/ContactQualificationAiQualification';
import { ScenarioSample } from '../../../../domain/models/scenario/ScenarioSample';
import Scenario from '../../../../domain/models/Scenario';

interface ScenarioCreationFormContextProps {
  status: ContextStatus;
  idDomain: string | null;
  name: string;
  nameShort: string;
  businessPriority: ScenarioBusinessPriority;
  description: string;
  constraints: string;
  criteria: string[];
  target: string;
  behavior: string;
  escalationRules: ContactQualificationAiQualification[];
  escalationNeedsQualification: boolean;
  fidelityModelFirstMessage: boolean;
  qualificationInstructions: string;
  samples: ScenarioSample[];
  link_calendar_booking: string;

  initWithDomainId: (domainId: string) => void;
  onNameChange: (name: string) => void;
  onNameShortChange: (nameShort: string) => void;
  onLinkCalendarBookingChange: (link: string) => void;
  submit: () => Promise<void>;
}

const ScenarioCreationFormContext =
  createContext<ScenarioCreationFormContextProps>({
    status: ContextStatus.IDLE,
    idDomain: null,
    name: '',
    nameShort: '',
    businessPriority: ScenarioBusinessPriority.one,
    description: '',
    constraints: '',
    criteria: [],
    target: '',
    behavior: '',
    escalationRules: [],
    escalationNeedsQualification: false,
    fidelityModelFirstMessage: false,
    qualificationInstructions: '',
    samples: [],
    link_calendar_booking: '',

    initWithDomainId: (_: string) => {},
    onNameChange: (_: string) => {},
    onNameShortChange: (_: string) => {},
    onLinkCalendarBookingChange: (_: string) => {},
    submit: async () => {},
  });

export const ScenarioCreationFormProvider: React.FC<
  React.PropsWithChildren
> = ({ children }) => {
  const [status, setStatus] = useState(ContextStatus.IDLE);

  const [idDomain, setIdDomain] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [nameShort, setNameShort] = useState('');
  const [businessPriority, setBusinessPriority] = useState(
    ScenarioBusinessPriority.one
  );
  const [description, setDescription] = useState('');
  const [constraints, setConstraints] = useState('');
  const [criteria, setCriteria] = useState<string[]>([]);
  const [target, setTarget] = useState('');
  const [behavior, setBehavior] = useState('');
  const [escalationRules, setEscalationRules] = useState<ContactQualificationAiQualification[]>([]);
  const [escalationNeedsQualification, setEscalationNeedsQualification] = useState(false);
  const [fidelityModelFirstMessage, setFidelityModelFirstMessage] = useState(false);
  const [qualificationInstructions, setQualificationInstructions] = useState('');
  const [samples, setSamples] = useState<ScenarioSample[]>([]);
  const [link_calendar_booking, setLinkCalendarBooking] = useState('');

  const authContext = useContext(AuthenticationContext);

  const initWithDomainId = (domainId: string) => {
    const newScenario = Scenario.newWithDomainId(domainId);
    setIdDomain(newScenario.idDomain);
    setName(newScenario.name);
    setNameShort(newScenario.nameShort);
    setBusinessPriority(newScenario.businessPriority);
    setDescription(newScenario.description);
    setConstraints(newScenario.constraints);
    setCriteria(newScenario.criteria);
    setTarget(newScenario.target);
    setBehavior(newScenario.behavior);
    setEscalationRules(newScenario.escalationRules);
    setEscalationNeedsQualification(newScenario.escalationNeedsQualification);
    setFidelityModelFirstMessage(newScenario.fidelityModelFirstMessage);
    setQualificationInstructions(newScenario.qualificationInstructions);
    setSamples(newScenario.samples);
    setLinkCalendarBooking(newScenario.link_calendar_booking);
  };

  const onNameChange = (newName: string) => setName(newName);
  const onNameShortChange = (newName: string) => setNameShort(newName);
  const onLinkCalendarBookingChange = (newLink: string) => setLinkCalendarBooking(newLink);

  const submit = async () => {
    if (!authContext.authentication || !idDomain) return;

    setStatus(ContextStatus.LOADING);

    if (name.length === 0) {
      console.error('Scenario name should not be empty.');
      setStatus(ContextStatus.ERROR);
      return;
    }

    if (nameShort.length === 0) {
      console.error('Scenario short name should not be empty.');
      setStatus(ContextStatus.ERROR);
      return;
    }

    try {
      const scenario = new Scenario(
        Scenario.generateScenarioId(),
        idDomain,
        name,
        nameShort,
        businessPriority,
        description,
        constraints,
        criteria,
        target,
        behavior,
        escalationRules,
        escalationNeedsQualification,
        fidelityModelFirstMessage,
        qualificationInstructions,
        samples,
        new Date(),
        new Date(),
        link_calendar_booking
      );

      await createScenario(scenario, authContext.authentication);
      setStatus(ContextStatus.SUCCESS);
    } catch (error) {
      console.error('Error creating scenario:', error);
      setStatus(ContextStatus.ERROR);
    }
  };

  return (
    <ScenarioCreationFormContext.Provider
      value={{
        status,
        idDomain,
        name,
        nameShort,
        businessPriority,
        description,
        constraints,
        criteria,
        target,
        behavior,
        escalationRules,
        escalationNeedsQualification,
        fidelityModelFirstMessage,
        qualificationInstructions,
        samples,
        link_calendar_booking,
        initWithDomainId,
        onNameChange,
        onNameShortChange,
        onLinkCalendarBookingChange,
        submit,
      }}
    >
      {children}
    </ScenarioCreationFormContext.Provider>
  );
};

export default ScenarioCreationFormContext;
